import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BaseUrl = process.env.REACT_APP_API_URL;

// ==================== PROJECT THUNKS ====================

// 1. Fetch all projects
export const fetchProjects = createAsyncThunk(
  "tableManagement/fetchProjects",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/show_project`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch projects"
      );
    }
  }
);

// 2. Add new project
export const addProject = createAsyncThunk(
  "tableManagement/addProject",
  async (projectName, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/add_project`,
        { project_name: projectName },
        { headers: { "Content-Type": "application/json" } }
      );
      // Immediately refresh projects list
      thunkAPI.dispatch(fetchProjects());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to add project"
      );
    }
  }
);

// 3. Update project name
export const updateProject = createAsyncThunk(
  "tableManagement/updateProject",
  async ({ id, projectName }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/update_project/${id}`,
        { project_name: projectName },
        { headers: { "Content-Type": "application/json" } }
      );
      // Refresh projects
      thunkAPI.dispatch(fetchProjects());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update project"
      );
    }
  }
);

// 4. Fetch details (types) in project
export const fetchProjectDetails = createAsyncThunk(
  "tableManagement/fetchProjectDetails",
  async (id, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/Add_detailsInProject/${id}`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch project details"
      );
    }
  }
);

// 5. Add / toggle type in project
export const addTypeInProject = createAsyncThunk(
  "tableManagement/addTypeInProject",
  async ({ projectId, typeId }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/AddTypeInProject/${projectId}/${typeId}`,
        { headers: { "Content-Type": "application/json" } }
      );
      // Re-fetch project list to update count
      thunkAPI.dispatch(fetchProjects());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update type in project"
      );
    }
  }
);

// ==================== TYPE THUNKS ====================

// 6. Fetch all types
export const fetchTypes = createAsyncThunk(
  "tableManagement/fetchTypes",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/show_Type`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch types"
      );
    }
  }
);

// 7. Add new type
export const addType = createAsyncThunk(
  "tableManagement/addType",
  async (typeName, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/add_Type`,
        { Type_name: typeName },
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchTypes());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to add type"
      );
    }
  }
);

// 8. Update type name
export const updateType = createAsyncThunk(
  "tableManagement/updateType",
  async ({ id, typeName }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/update_Type/${id}`,
        { Type_name: typeName },
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchTypes());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update type"
      );
    }
  }
);

// 9. Fetch details (modules) in type
export const fetchTypeDetails = createAsyncThunk(
  "tableManagement/fetchTypeDetails",
  async (id, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/Add_detailsInType/${id}`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch type details"
      );
    }
  }
);

// 10. Add / toggle module in type
export const addModuleInType = createAsyncThunk(
  "tableManagement/addModuleInType",
  async ({ typeId, moduleId }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/AddTypeInType/${typeId}/${moduleId}`,
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchTypes());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update module in type"
      );
    }
  }
);

// ==================== MODULE THUNKS ====================

// 11. Fetch all modules
export const fetchModules = createAsyncThunk(
  "tableManagement/fetchModules",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/show_module`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch modules"
      );
    }
  }
);

// 12. Add new module
export const addModule = createAsyncThunk(
  "tableManagement/addModule",
  async (moduleName, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/add_module`,
        { module_name: moduleName },
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchModules());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to add module"
      );
    }
  }
);

// 13. Update module name
export const updateModule = createAsyncThunk(
  "tableManagement/updateModule",
  async ({ id, moduleName }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/update_module/${id}`,
        { module_name: moduleName },
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchModules());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update module"
      );
    }
  }
);

// 14. Fetch details (tables) in module
export const fetchModuleDetails = createAsyncThunk(
  "tableManagement/fetchModuleDetails",
  async (id, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/Add_detailsInmodule/${id}`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch module details"
      );
    }
  }
);

// 15. Add / toggle table in module
export const addTableInModule = createAsyncThunk(
  "tableManagement/addTableInModule",
  async ({ moduleId, tableId }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/AddTypeInmodule/${moduleId}/${tableId}`,
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchModules());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to update table in module"
      );
    }
  }
);

// ==================== TABLE THUNKS ====================

// 16. Fetch all tables
export const fetchAllTables = createAsyncThunk(
  "tableManagement/fetchAllTables",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/show_detail`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch tables"
      );
    }
  }
);

// 17. Fetch single table details (fields)
export const fetchTableDetails = createAsyncThunk(
  "tableManagement/fetchTableDetails",
  async (id, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BaseUrl}/TablemangeController/show_detail/${id}`,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch table details"
      );
    }
  }
);

// 18. Add table with fields
export const addTableWithFields = createAsyncThunk(
  "tableManagement/addTableWithFields",
  async ({ tableName, fieldNames }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/TablemangeController/add_detail`,
        { tname: tableName, fname: fieldNames },
        { headers: { "Content-Type": "application/json" } }
      );
      thunkAPI.dispatch(fetchAllTables());
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Failed to create table"
      );
    }
  }
);

// ==================== SLICE ====================

const TableManagementSlice = createSlice({
  name: "tableManagement",
  initialState: {
    // Projects
    projects: null,
    projectsLoading: false,
    projectsError: null,
    projectDetails: null,
    projectDetailsLoading: false,

    // Types
    types: null,
    typesLoading: false,
    typesError: null,
    typeDetails: null,
    typeDetailsLoading: false,

    // Modules
    modules: null,
    modulesLoading: false,
    modulesError: null,
    moduleDetails: null,
    moduleDetailsLoading: false,

    // Tables
    tables: null,
    tablesLoading: false,
    tablesError: null,
    selectedTable: null,
    selectedTableLoading: false,

    // Action status message (replaces tdata)
    statusMessage: null,
    actionLoading: false,
    actionError: null,
  },

  reducers: {
    clearStatusMessage: (state) => {
      state.statusMessage = null;
      state.actionError = null;
    },
    clearSelectedProject: (state) => {
      state.projectDetails = null;
    },
    clearSelectedType: (state) => {
      state.typeDetails = null;
    },
    clearSelectedModule: (state) => {
      state.moduleDetails = null;
    },
    clearSelectedTable: (state) => {
      state.selectedTable = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ---------- Projects ----------
      .addCase(fetchProjects.pending, (state) => {
        state.projectsLoading = true;
        state.projectsError = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projectsLoading = false;
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.projectsLoading = false;
        state.projectsError = action.payload;
      })

      .addCase(addProject.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(addProject.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Project added successfully";
      })
      .addCase(addProject.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      .addCase(updateProject.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Project updated successfully";
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      .addCase(fetchProjectDetails.pending, (state) => {
        state.projectDetailsLoading = true;
      })
      .addCase(fetchProjectDetails.fulfilled, (state, action) => {
        state.projectDetailsLoading = false;
        state.projectDetails = action.payload;
      })
      .addCase(fetchProjectDetails.rejected, (state, action) => {
        state.projectDetailsLoading = false;
        state.actionError = action.payload;
      })

      .addCase(addTypeInProject.fulfilled, (state, action) => {
        state.projectDetails = action.payload;
        state.statusMessage = action.payload?.status || "Project types updated";
      })

      // ---------- Types ----------
      .addCase(fetchTypes.pending, (state) => {
        state.typesLoading = true;
        state.typesError = null;
      })
      .addCase(fetchTypes.fulfilled, (state, action) => {
        state.typesLoading = false;
        state.types = action.payload;
      })
      .addCase(fetchTypes.rejected, (state, action) => {
        state.typesLoading = false;
        state.typesError = action.payload;
      })

      .addCase(addType.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addType.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Type added successfully";
      })
      .addCase(addType.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      .addCase(updateType.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateType.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Type updated successfully";
      })
      .addCase(updateType.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      .addCase(fetchTypeDetails.pending, (state) => {
        state.typeDetailsLoading = true;
      })
      .addCase(fetchTypeDetails.fulfilled, (state, action) => {
        state.typeDetailsLoading = false;
        state.typeDetails = action.payload;
      })
      .addCase(fetchTypeDetails.rejected, (state, action) => {
        state.typeDetailsLoading = false;
        state.actionError = action.payload;
      })

      .addCase(addModuleInType.fulfilled, (state, action) => {
        state.typeDetails = action.payload;
        state.statusMessage = action.payload?.status || "Type modules updated";
      })

      // ---------- Modules ----------
      .addCase(fetchModules.pending, (state) => {
        state.modulesLoading = true;
        state.modulesError = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.modulesLoading = false;
        state.modules = action.payload;
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.modulesLoading = false;
        state.modulesError = action.payload;
      })

      .addCase(addModule.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addModule.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Module added successfully";
      })
      .addCase(addModule.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      .addCase(updateModule.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateModule.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Module updated successfully";
      })
      .addCase(updateModule.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      .addCase(fetchModuleDetails.pending, (state) => {
        state.moduleDetailsLoading = true;
      })
      .addCase(fetchModuleDetails.fulfilled, (state, action) => {
        state.moduleDetailsLoading = false;
        state.moduleDetails = action.payload;
      })
      .addCase(fetchModuleDetails.rejected, (state, action) => {
        state.moduleDetailsLoading = false;
        state.actionError = action.payload;
      })

      .addCase(addTableInModule.fulfilled, (state, action) => {
        state.moduleDetails = action.payload;
        state.statusMessage = action.payload?.status || "Module tables updated";
      })

      // ---------- Tables ----------
      .addCase(fetchAllTables.pending, (state) => {
        state.tablesLoading = true;
        state.tablesError = null;
      })
      .addCase(fetchAllTables.fulfilled, (state, action) => {
        state.tablesLoading = false;
        state.tables = action.payload;
      })
      .addCase(fetchAllTables.rejected, (state, action) => {
        state.tablesLoading = false;
        state.tablesError = action.payload;
      })

      .addCase(fetchTableDetails.pending, (state) => {
        state.selectedTableLoading = true;
      })
      .addCase(fetchTableDetails.fulfilled, (state, action) => {
        state.selectedTableLoading = false;
        state.selectedTable = action.payload;
      })
      .addCase(fetchTableDetails.rejected, (state, action) => {
        state.selectedTableLoading = false;
        state.actionError = action.payload;
      })

      .addCase(addTableWithFields.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addTableWithFields.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.statusMessage = action.payload?.status || "Table and fields created successfully";
      })
      .addCase(addTableWithFields.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const {
  clearStatusMessage,
  clearSelectedProject,
  clearSelectedType,
  clearSelectedModule,
  clearSelectedTable,
} = TableManagementSlice.actions;

export default TableManagementSlice.reducer;
